<template>
  <v-card flat :loading="disabled" :disabled="disabled">
    <v-card-text class="mt-1 pt-0">
      <h3 id="cast_sync_step3">取込の確認</h3>
      <v-subheader
        >下記のセラピストを処理します。選択に誤りがある場合は、ひとつ前の画面で修正して下さい。</v-subheader
      >
      <v-container>
        <v-row>
          <v-col cols="12" class="pb-0">
            <!-- 選択した取込内容のサマリー -->
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <th class="text-left">選択媒体</th>
                    <td>{{ shopSiteName }}</td>
                  </tr>
                  <tr>
                    <th class="text-left">セラピスト画像の取込方法</th>
                    <td>{{ overwriteImageLabel }}</td>
                  </tr>
                  <tr v-if="siteOnlyItemsLabel">
                    <th class="text-left">取込オプション</th>
                    <td>{{ siteOnlyItemsLabel }}</td>
                  </tr>
                  <tr>
                    <th class="text-left">取込人数</th>
                    <td>{{ castCountLabel }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <!-- テーブル -->
            <v-data-table
              class="sync_table"
              :headers="headers"
              :items="desserts"
              :mobile-breakpoint="0"
              item-key="primary_key"
              :footer-props="{ 'items-per-page-options': [-1] }"
              hide-default-footer
            >
              <template v-slot:no-data> 表示するデータがありません </template>
              <template v-slot:item.name="{ item }"> <!-- eslint-disable-line -->
                <template
                  ><v-avatar class="ma-1 mr-3"
                    ><v-img v-if="item.image" :src="setting.cdn + item.image" />
                    <v-img
                      v-else
                      src="@/assets/images/no_image.jpg" /></v-avatar
                  >{{ item.display_label }}</template
                >
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="3">
            <general-button btn_type="gray" btn_block @click-event="onCancel"
              >キャンセル</general-button
            >
          </v-col>
          <v-col cols="6" sm="3"> </v-col>
          <v-col cols="6" sm="3">
            <general-button btn_type="gray" btn_block @click-event="onBack"
              >戻る</general-button
            >
          </v-col>
          <v-col cols="6" sm="3">
            <general-button
              btn_type="info"
              btn_block
              :btn_disabled="desserts.length == 0"
              @click-event="onSubmit"
              >取込処理実行</general-button
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { reactive, toRefs, defineComponent, set } from "@vue/composition-api";
import store from "@/store/index.js";
import setting from "@/common/setting.js";
import { repositoryFactory } from "@/repository/repositoryFactory";
import ModelShopSite from "@/model/shopSite.js";

export default defineComponent({
  setup(_, ctx) {
    const SynchroRepository = repositoryFactory.get("synchro");
    const state = reactive({
      shopSiteId: 0,
      siteOnlyItems: [],
      overwriteImage: false,
      shopSiteName: "",
      overwriteImageLabel: "",
      siteOnlyItemsLabel: "",
      castCountLabel: "",
      summaryText: "",
      disabled: false,
      headers: [
        {
          text: "媒体に登録してあるセラピスト",
          value: "name",
          sortable: true,
        },
        {
          text: "取込内容",
          value: "status",
          sortable: true,
        },
      ],
      desserts: [], // DataTablesレコード格納用
    });

    // 初期化処理(同期処理)
    const init = async () => {
      // ローディング表示
      store.dispatch("loadingIcon/showIcon");
      state.disabled = true;

      state.shopSiteId = store.getters["synchro_cast/getSelectedShopSiteId"];
      state.siteOnlyItems = store.getters["synchro_cast/getSiteOnlyItems"];
      state.overwriteImage = store.getters["synchro_cast/getOverwriteImage"];

      // ストアの配列をそのまま保持するとstep2の選択リストと同一参照になり、
      // 再度この画面を開いた時のsplice(0)でストア側まで空になるため、コピーして詰め直す
      state.desserts.splice(0);
      const tmp = store.getters["synchro_cast/getSelectedCastList"];
      tmp
        .slice()
        .sort((first, second) => first - second)
        .forEach((row) => {
          state.desserts.push(row);
        });

      // 選択した取込内容をサマリー表示用の文言に変換する
      const shopSite = store.getters["shop_sites/getShopSiteById"](
        state.shopSiteId
      );
      state.shopSiteName = shopSite ? shopSite.name : "";
      state.overwriteImageLabel = state.overwriteImage
        ? "上書きする"
        : "上書きしない（更新のみ）";
      const siteOnlyLabels = [];
      if (state.siteOnlyItems.includes("comment")) {
        siteOnlyLabels.push("お店コメント・セラピストコメント");
      }
      if (state.siteOnlyItems.includes("image")) {
        siteOnlyLabels.push("セラピスト画像");
      }
      // 未指定の場合は「取り込みしない」と誤解されるため、文言を空にして項目自体を表示しない
      state.siteOnlyItemsLabel = siteOnlyLabels.length
        ? "媒体個別情報として" + siteOnlyLabels.join("、") + "を取り込む"
        : "";
      // 取込内容(status)は step2 で "新規登録" / "更新" のいずれかが入る
      const newCount = state.desserts.filter(
        (r) => r.status == "新規登録"
      ).length;
      state.castCountLabel =
        "新規登録: " +
        newCount +
        "名、更新: " +
        (state.desserts.length - newCount) +
        "名";
      // 取込完了メッセージにも同じ内容を添える
      state.summaryText =
        "選択媒体：" +
        state.shopSiteName +
        "\nセラピスト画像の取込方法：" +
        state.overwriteImageLabel +
        (state.siteOnlyItemsLabel
          ? "\n取込オプション：" + state.siteOnlyItemsLabel
          : "") +
        "\n取込人数：" +
        state.castCountLabel;

      // ローディング非表示
      store.dispatch("loadingIcon/hideIcon");
      state.disabled = false;
    };

    // キャスト同期処理
    const castSync = async () => {
      // ローディング表示
      state.disabled = true;
      store.dispatch("loadingIcon/showIcon");

      let result = false;
      const params = {
        shop_site_id: state.shopSiteId,
        site_only_items: state.siteOnlyItems,
        overwrite_image: state.overwriteImage,
        cast_list: state.desserts,
      };
      result = await SynchroRepository.sync_update_cast(params)
        .then((response) => {
          if (response.data) {
            if (response.data.status) {
              // 取込結果に、実行した取込内容のサマリーを添えて表示する
              alert(response.data.message + "\n\n" + state.summaryText);
              return true;
            } else {
              alert(response.data.message);
              return false;
            }
          }
        })
        .catch((error) => {
          throw (
            "ERROR:Step3.vue/castSync SynchroRepository.sync_update_cast (" +
            error +
            ")"
          );
        });

      // ローディング非表示
      store.dispatch("loadingIcon/hideIcon");
      state.disabled = false;

      return result;
    };

    const onCancel = () => {
      ctx.emit("click-cancel");
    };
    const onBack = () => {
      ctx.emit("click-back");
    };
    const onSubmit = async () => {
      if (!window.confirm("取込処理を開始します。よろしいですか？")) {
        return;
      }
      // キャスト取り込み処理
      if (await castSync()) {
        // 更新が完了したらキャスト&店舗サイトを取り直す
        await ModelShopSite.setStore();
        // await ModelCast.setStore();
        ctx.emit("sync-ok");
      }
    };

    return {
      setting,
      ...toRefs(state),
      init,
      onSubmit,
      onCancel,
      onBack,
    };
  },
});
</script>
